import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { TextFilterDef, TextFilterValue } from '../filterTypes';
import type { FilterFieldProps } from '../FilterField.types';
import { FieldLabel } from '../FilterFieldHelpers';
import { textFieldSx } from '../styles/filterColumnSx';
import '../styles/filterPanel.css';

export function TextFilterInput({
  def,
  value,
  onChange,
  onClear,
  variant = 'panel',
  label,
}: FilterFieldProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isColumn = variant === 'column';
  const currentValue = (value as TextFilterValue | undefined)?.value ?? '';
  const hasValue = currentValue !== '';
  const textDef = def as TextFilterDef;

  const field = (
    <TextField
      size="small"
      fullWidth={!isColumn}
      type="text"
      value={currentValue}
      placeholder={
        isColumn
          ? (textDef.inputProps?.placeholder as string | undefined)
          : t('table.filter.searchPlaceholder')
      }
      onChange={e => {
        if (!e.target.value) onClear();
        else onChange({ kind: 'text', value: e.target.value });
      }}
      {...(isColumn ? { sx: textFieldSx(theme) } : { className: 'filter-input' })}
      slotProps={{
        // The def's input attributes (aria-label, maxLength…), as NumericFilterInput does.
        htmlInput: { ...textDef.inputProps },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{ fontSize: isColumn ? '0.8rem' : '0.85rem', color: 'action.disabled' }}
              />
            </InputAdornment>
          ),
          ...(hasValue && {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={onClear}
                  aria-label={t('table.filter.clear')}
                  edge="end"
                  tabIndex={-1}
                  sx={{ p: 0.25 }}
                >
                  <ClearIcon sx={{ fontSize: isColumn ? '0.8rem' : undefined }} />
                </IconButton>
              </InputAdornment>
            ),
          }),
        },
      }}
    />
  );

  if (isColumn) return field;
  return (
    <Box className="filter-field">
      {label && <FieldLabel>{label}</FieldLabel>}
      {field}
    </Box>
  );
}
